import { postDataAPI, getDataAPI } from './api'

export interface Message {
  role: 'user' | 'assistant'
  content: string
  timestamp?: string
}

export interface ChatResponse {
  success: boolean
  response: string
  conversationId?: string
  conversation?: Message[]
  error?: string
}

export interface PresetPrompt {
  title: string
  content: string
  category?: string
  order?: number
}

/**
 * Default static prompts for the chat (relevant to the new system)
 */
export const DEFAULT_PROMPTS: PresetPrompt[] = [
  { title: 'Show my upcoming schedule', content: 'Show my upcoming schedule' },
  { title: 'What are my current tasks?', content: 'What are my current tasks?' },
  { title: 'Create a new task', content: 'Create a new task' },
  { title: 'Show my recent shows', content: 'Show my recent shows' },
  { title: 'View my music releases', content: 'View my music releases' },
  { title: 'Financial overview', content: 'Financial overview' },
  { title: 'Team tasks and assignments', content: 'Team tasks and assignments' },
  { title: 'Career health check', content: 'Career health check' },
  { title: 'Upcoming tour dates', content: 'Upcoming tour dates' },
  { title: 'Help me plan content', content: 'Help me plan content' },
]

/**
 * Send a message to the AI chat API (matching legacy processQuestionForDizee)
 */
export const sendChatMessage = async (
  question: string,
  conversationHistory: Message[] = [],
  conversationId: string = '',
  images: any[] = []
): Promise<ChatResponse> => {
  try {
    const token = localStorage.getItem('dizeeToken')
    
    // Convert message format to match backend expectations
    const formattedHistory = conversationHistory.map((msg) => ({
      type: msg.role,
      content: msg.content,
    }))

    // Get AI mode status from localStorage (default true)
    const aiModeEnabled = JSON.parse(localStorage.getItem('dizeeAIModeEnabled') ?? 'true')

    const payload = {
      prompt: question,
      conversation: formattedHistory,
      conversationId: conversationId.trim() !== '' ? conversationId : '',
      images: images.map(img => ({
        url: img.url,
        name: img.name,
        type: img.fileType
      })),
      aiModeEnabled: aiModeEnabled,
    }

    console.log('🚀 Sending chat message to API:', {
      prompt: question.substring(0, 100),
      conversationId: payload.conversationId,
      historyLength: formattedHistory.length,
      imagesCount: images.length,
    })

    const response = await postDataAPI('user/chatWithAIForDizee', payload, token || undefined)

    console.log('✅ Chat API response received:', response)

    // Handle nested data structure from backend
    const responseData = response.data || response
    const aiResponse = responseData.data?.response || responseData.response
    const returnedConversationId = responseData.data?.conversationId || responseData.conversationId
    const conversation = responseData.data?.conversation || responseData.conversation

    if (aiResponse) {
      return {
        success: true,
        response: aiResponse,
        conversationId: returnedConversationId,
        conversation: conversation,
      }
    } else {
      console.error('❌ No response in API data:', responseData)
      return {
        success: false,
        response: 'Something went wrong. Please try again.',
        error: 'No response from AI',
      }
    }
  } catch (error: any) {
    console.error('❌ Chat API Error:', error)
    return {
      success: false,
      response: 'Failed to connect to AI service. Please try again.',
      error: error.response?.data?.message || error.message || 'Unknown error',
    }
  }
}

/**
 * Get conversation history by ID
 */
export const getConversationById = async (conversationId: string) => {
  try {
    const token = localStorage.getItem('dizeeToken')
    const response = await getDataAPI(`user/conversations/${conversationId}`, token || undefined)
    
    if (response?.success) {
      return {
        success: true,
        messages: response.messages || [],
      }
    }
    
    return {
      success: false,
      messages: [],
    }
  } catch (error) {
    console.error('Error fetching conversation:', error)
    return {
      success: false,
      messages: [],
    }
  }
}

/**
 * Get user's conversation history
 */
export const getConversationHistory = async () => {
  try {
    const token = localStorage.getItem('dizeeToken')
    const response = await getDataAPI('user/conversations', token || undefined)
    
    if (response?.success) {
      return {
        success: true,
        conversations: response.conversations || [],
      }
    }
    
    return {
      success: false,
      conversations: [],
    }
  } catch (error) {
    console.error('Error fetching conversation history:', error)
    return {
      success: false,
      conversations: [],
    }
  }
}
